import React from 'react';
import { ArrowRight, Shield, TrendingUp, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

const Hero = () => {
  return (
    <section id="home" className="relative h-[70vh] lg:h-[110vh] xl:h-[120vh] flex items-center justify-center overflow-hidden pt-12 lg:pt-28">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="46285-446732353_tiny.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-navy-900/40"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/50 to-navy-800/30"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-4 sm:py-6 lg:py-8 xl:py-12">
        <div className="max-w-4xl mx-auto mt-5">
          {/* Main Heading */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-bold font-serif text-white mb-4 sm:mb-5 lg:mb-4 xl:mb-6 leading-tight animate-slide-up animation-delay-200 drop-shadow-lg">
            Elevate Your
            <span className="text-gradient block mt-1 sm:mt-2 lg:mt-2 xl:mt-3 animate-glow-pulse">
              Financial Future
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-base sm:text-lg md:text-xl lg:text-lg xl:text-xl text-gray-200 mb-6 sm:mb-8 lg:mb-6 xl:mb-8 max-w-3xl mx-auto leading-relaxed animate-fade-in animation-delay-400 px-2 sm:px-0 drop-shadow-md">
            Hula Capital delivers expert investment solutions across
            <span className="text-gold-400 font-semibold"> Blockchain-based Assets, Real Estate, Indices, and Stocks</span>. 
            Unlock your wealth potential through strategic portfolio management and data-driven market insights.
          </p>

          {/* CTA Button */}
          <div className="flex justify-center items-center mb-6 sm:mb-8 lg:mb-8 xl:mb-10 animate-scale-in animation-delay-600 px-4 sm:px-0">
            <Link 
              to="/services"
              className="group relative inline-flex items-center justify-center px-8 sm:px-12 py-4 sm:py-5 text-base sm:text-lg font-bold text-white bg-gradient-to-r from-gold-500 via-gold-600 to-gold-700 rounded-full shadow-2xl hover:shadow-gold-500/25 transition-all duration-500 hover:scale-105 hover:-translate-y-1 overflow-hidden"
            >
              {/* Animated background gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-gold-600 via-gold-700 to-gold-800 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Shine effect */}
              <div className="absolute inset-0 -top-full group-hover:top-full bg-gradient-to-b from-transparent via-white/20 to-transparent transition-all duration-700 transform skew-x-12"></div>
              
              {/* Button content */}
              <span className="relative z-10 mr-3">Explore Services</span>
              <ArrowRight className="relative z-10 h-5 w-5 sm:h-6 sm:w-6 group-hover:translate-x-2 transition-transform duration-300" />
              
              {/* Pulse ring */}
              <div className="absolute inset-0 rounded-full bg-gold-400/30 animate-ping"></div>
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 md:gap-6 lg:gap-8 max-w-lg sm:max-w-2xl mx-auto animate-fade-in animation-delay-800">
            <div className="text-center group bg-white/10 backdrop-blur-md rounded-lg p-2 sm:p-4 md:p-6 lg:p-3 border border-white/20 hover:bg-white/15 transition-all duration-300 shadow-lg">
              <div className="flex items-center justify-center mb-1 sm:mb-2">
                <TrendingUp className="h-4 w-4 sm:h-6 sm:w-6 md:h-8 md:w-8 lg:h-6 lg:w-6 text-gold-400 group-hover:scale-110 transition-transform" />
              </div>
              <div className="text-sm sm:text-xl md:text-2xl lg:text-xl font-bold text-white">$4.2B+</div>
              <div className="text-gray-300 text-xs sm:text-sm md:text-base lg:text-sm">Assets Under Management</div>
            </div>
            <div className="text-center group bg-white/10 backdrop-blur-md rounded-lg p-2 sm:p-4 md:p-6 lg:p-3 border border-white/20 hover:bg-white/15 transition-all duration-300 shadow-lg">
              <div className="flex items-center justify-center mb-1 sm:mb-2">
                <Users className="h-4 w-4 sm:h-6 sm:w-6 md:h-8 md:w-8 lg:h-6 lg:w-6 text-gold-400 group-hover:scale-110 transition-transform" />
              </div>
              <div className="text-sm sm:text-xl md:text-2xl lg:text-xl font-bold text-white">40K+</div>
              <div className="text-gray-300 text-xs sm:text-sm md:text-base lg:text-sm">Active Clients</div>
            </div>
            <div className="text-center group bg-white/10 backdrop-blur-md rounded-lg p-2 sm:p-4 md:p-6 lg:p-3 border border-white/20 hover:bg-white/15 transition-all duration-300 shadow-lg">
              <div className="flex items-center justify-center mb-1 sm:mb-2">
                <Shield className="h-4 w-4 sm:h-6 sm:w-6 md:h-8 md:w-8 lg:h-6 lg:w-6 text-gold-400 group-hover:scale-110 transition-transform" />
              </div>
              <div className="text-sm sm:text-xl md:text-2xl lg:text-xl font-bold text-white">12+</div>
              <div className="text-gray-300 text-xs sm:text-sm md:text-base lg:text-sm">Years Experience</div>
            </div>
          </div>
        </div>
      </div>


    </section>
  );
};

export default Hero;